import React, { useEffect, useState, useRef } from "react";
import { Box, Button } from "@mui/material";
import SidebarMenu from "./Sidebar";
import RoutesConfig from "../../routes";
import { API } from "aws-amplify";
import Auth from "@aws-amplify/auth";
import * as queries from "../../graphql/queries";
import { UserContext } from "../../contexts";
import { Steps } from "../onboarding/Steps";
import Joyride from "react-joyride";
import Topbar from "./Topbar";
import CustomSnackbar from "../Snackbar";
import Chatbot from "../Chatbot";
import ChatbotPopup from "../ChatbotPopup";
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from "../../features/settings/userSlice";
import { selectMetadata } from "../../features/settings/settingsSlice";
import { fetchUserAssets } from "../../features/userAssets/userAssetsSlice";
import { setIsAdmin, setGroupsLoaded, setGroups, clearAuth, selectIsAdmin } from "../../features/auth/authSlice";
import { useProSidebar } from "react-pro-sidebar";
import { useLocation } from "react-router-dom";
import ErrorBoundary from "../ErrorBoundary";

const Layout = ({ user, signOut }) => {
  const [open, setOpen] = React.useState(true);
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const { collapseSidebar } = useProSidebar();
  const location = useLocation();
  const isAdmin = useSelector(selectIsAdmin);
  const [userAppSettings, setUserAppSettings] = useState({});
  const [myQueries, setMyQueries] = useState([]);
  const [sharedQueries, setSharedQueries] = useState([]);
  const [runOnboarding, setRunOnboarding] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [snackbarInfo, setSnackbarInfo] = useState({ open: false, severity: 'info', message: '' });
  const { userAssets, loading: userAssetsLoading, loaded: userAssetsLoaded, loadedForUserId, error: userAssetsError } = useSelector((store) => store.userAssets);
  const dispatch = useDispatch();
  const metadata = useSelector(selectMetadata);

  useEffect(() => {
    fetchUserAppSettings();
  }, []);

  useEffect(() => {
    const userId = user?.attributes?.sub;
    if (userId && !userAssetsLoading && (!userAssetsLoaded || loadedForUserId !== userId)) {
      dispatch(fetchUserAssets(userId));
    }
  }, [user?.attributes?.sub, userAssetsLoaded, userAssetsLoading, loadedForUserId, dispatch]);

  useEffect(() => {
    if (userAssetsError) {
      setSnackbarInfo({
        open: true,
        severity: 'error',
        message: `Couldn’t load user assets. ${userAssetsError || ''}`.trim(),
      });
    }
  }, [userAssetsError]);

  const sideBarRef = useRef();

  const fetchUserAppSettings = async () => {
    try {
      if (user?.attributes && user?.attributes?.sub) {
        const userSettings = await API.graphql({
          query: queries.getUserAppSettings,
          variables: { id: user?.attributes?.sub },
        });
        if (userSettings && userSettings.data) {
          setUserAppSettings(userSettings?.data?.getUserAppSettings);
          dispatch(setUser(userSettings?.data?.getUserAppSettings))
        }

        // check for Admins group in JWT token
        try {
          const session = await Auth.currentSession();
          const idToken = session.getIdToken();
          const groups = idToken.payload['cognito:groups'] || [];
          const isAdmin = groups.includes('ADMINS');
          console.log({ groups })
          dispatch(setIsAdmin(isAdmin));
          dispatch(setGroups(groups));
          dispatch(setGroupsLoaded(true));
        } catch (authError) {
          console.log('Error checking admin status:', authError);
          dispatch(setGroupsLoaded(true));
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarInfo((s) => ({ ...s, open: false }));
  };

  const handleRetry = () => {
    if (user?.attributes?.sub && !userAssetsLoading) {
      dispatch(fetchUserAssets(user.attributes.sub));
    }
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleSignOut = async () => {
    dispatch(clearAuth());
    await signOut();
  };

  return (
    <>
      <Box className="app">
        <SidebarMenu
          metadata={metadata}
          ref={sideBarRef}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
        />
        <main className="content">
          <Box sx={{ flexShrink: 0 }}>
            <Topbar
              user={user}
              signOut={handleSignOut}
              toggleDrawer={toggleDrawer}
              open={open}
              setRunOnboarding={setRunOnboarding}
              runOnboarding={runOnboarding}
              setSnackbarInfo={setSnackbarInfo}
            />
          </Box>
          <Box className="content-main"
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.default',
              color: 'text.primary'
            }}
          >
            <UserContext.Provider
              value={{
                myQueries,
                setMyQueries,
                sharedQueries,
                setSharedQueries,
                userAppSettings,
                setUserAppSettings,
                signOut: handleSignOut,
                activeStep,
                setActiveStep,
                snackbarInfo,
                setSnackbarInfo
              }}
            >
              <ErrorBoundary key={window.location.pathname}>
                <RoutesConfig
                  signOut={handleSignOut}
                  user={user}
                  handleMenuSelection={sideBarRef.current?.handleMenuSelection}
                />
              </ErrorBoundary>
            </UserContext.Provider>
          </Box>
        </main>
        {isAdmin && location.pathname === '/rwe-ai' ? (
          <Chatbot
            user={user}
            open={chatbotOpen}
            onOpen={() => { setChatbotOpen(true); collapseSidebar(true); }}
            onClose={() => { setChatbotOpen(false); collapseSidebar(false); }}
          />
        ) : (
          <ChatbotPopup user={user} />
        )}
      </Box>
      <Joyride
        steps={Steps}
        continuous
        scrollToFirstStep
        showProgress
        showSkipButton
        run={runOnboarding}
      />
      <CustomSnackbar
        severity={snackbarInfo.severity}
        message={snackbarInfo.message}
        open={snackbarInfo.open}
        handleClose={handleSnackbarClose}
        action={
          <span style={{ display: 'inline-block' }}>
            <Button
              onClick={handleRetry}
              size="small"
              color="inherit"
              disabled={userAssetsLoading}
            >
              Retry
            </Button>
          </span>
        }
      />
    </>
  );
};

export default Layout;
